import { callExternalApi } from "./external-api.service";

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

/**
 * Send a unified chat message (cohort, individual summary, or individual records)
 */
export const sendCohortChatMessage = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chat/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get task status for async response generation
 */
export const getCohortTaskStatus = async (token, taskId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/tasks/${taskId}/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Create a new conversation (metadata only) so it appears in the sidebar immediately
 */
export const createCohortConversation = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/create/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Generate LLM-backed starter or follow-up suggestions for cohort chat.
 */
export const generateCohortChatSuggestions = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/cohort-chat/suggestions/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Generate and persist an LLM-backed conversation title from the first exchange.
 */
export const generateCohortChatTitle = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/cohort-chat/title/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * List all conversations for the current user
 */
export const listCohortConversations = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get conversation history
 */
export const getCohortConversationHistory = async (token, conversationId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/${conversationId}/history/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const exportTable = async (
  token,
  conversationId,
  sequenceId,
  blockIndex,
  { reexecute = false } = {},
) => {
  const queryParams = new URLSearchParams({ format: 'csv' });
  if (reexecute) queryParams.append('reexecute', 'true');
  const url = (
    `${apiServerUrl}/api/admin/cohorts/chats/${conversationId}` +
    `/messages/${sequenceId}/tables/${blockIndex}/export/?${queryParams}`
  );

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const text = await response.text();
      return { data: null, error: text || 'Failed to export table' };
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    let filename = 'chat-table.csv';
    const disposition = response.headers.get('content-disposition');
    const match = disposition && disposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    return { data: { success: true, filename }, error: null };
  } catch (error) {
    return { data: null, error: error.message || 'Failed to export table' };
  }
};

/**
 * Update conversation metadata (e.g., rename)
 */
export const updateCohortConversation = async (token, conversationId, updates) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/${conversationId}/update/`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(updates),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Delete a conversation
 */
export const deleteCohortConversation = async (token, conversationId) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/${conversationId}/delete/`,
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Edit a message and regenerate response
 */
export const editCohortMessage = async (token, conversationId, sequenceId, newMessage) => {
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/${conversationId}/messages/${sequenceId}/edit/`,
    method: "PUT",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify({ message: newMessage }),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get context information (studies, versions, members count)
 */
export const getCohortChatContext = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.data_plane) queryParams.append('data_plane', params.data_plane);
  if (params.study_id) queryParams.append('study_id', params.study_id);
  if (params.version_label) queryParams.append('version_label', params.version_label);
  
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chat/context/?${queryParams}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * List members (for individual mode selection)
 */
export const getCohortMembers = async (token, params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.study_id) queryParams.append('study_id', params.study_id);
  if (params.search) queryParams.append('search', params.search);
  if (params.limit) queryParams.append('limit', params.limit);
  
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/members/?${queryParams}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get member details
 */
export const getMemberDetails = async (token, userId, dataPlane = 'customer') => {
  const query = dataPlane ? `?data_plane=${encodeURIComponent(dataPlane)}` : '';
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/members/${userId}${query}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getMemberRecordVectorDocument = async (token, { memberId, docId, datasetName }) => {
  const queryParams = new URLSearchParams({
    member_id: memberId,
    doc_id: docId,
  });
  if (datasetName) queryParams.append('dataset_name', datasetName);
  const config = {
    url: `${apiServerUrl}/api/admin/cohorts/chats/records/document/?${queryParams}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

export const getMemberRecordOriginalBlob = async (token, { memberId, docId, datasetName }) => {
  const queryParams = new URLSearchParams({
    member_id: String(memberId),
    doc_id: String(docId),
    dataset_name: String(datasetName || ''),
  });
  const response = await fetch(
    `${apiServerUrl}/api/admin/cohorts/chats/records/original/?${queryParams}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    let message = 'Failed to load original record';
    try {
      const body = await response.json();
      message = body.error || message;
    } catch (_err) {
      message = response.statusText || message;
    }
    return { blob: null, contentType: '', error: message };
  }
  const blob = await response.blob();
  return {
    blob,
    contentType: response.headers.get('content-type') || blob.type || '',
    error: null,
  };
};

/**
 * Get summary versions
 */
export const getSummaryVersions = async (token, dataPlane = 'customer') => {
  const query = dataPlane ? `?data_plane=${encodeURIComponent(dataPlane)}` : '';
  const config = {
    url: `${apiServerUrl}/api/admin/summaries/versions${query}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Register an S3 folder as a chat dataset
 */
export const registerDataset = async (token, payload) => {
  const config = {
    url: `${apiServerUrl}/api/datasets/register/`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Update an existing chat dataset
 */
export const updateDataset = async (token, datasetId, payload) => {
  const config = {
    url: `${apiServerUrl}/api/datasets/${datasetId}/`,
    method: "PATCH",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: JSON.stringify(payload),
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get datasets with metadata and allowed_scopes
 */
export const getDatasets = async (token, dataPlane = 'customer') => {
  const query = `?data_plane=${encodeURIComponent(dataPlane)}`;
  const config = {
    url: `${apiServerUrl}/api/datasets/${query}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get members (user IDs) available in a specific dataset
 */
export const getDatasetMembers = async (token, datasetId, dataPlane = 'customer') => {
  const query = `?data_plane=${encodeURIComponent(dataPlane)}`;
  const config = {
    url: `${apiServerUrl}/api/datasets/${datasetId}/members${query}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Check if a member exists in the default dataset; triggers pipeline if not.
 */
export const checkMemberDefaultDataset = async (token, memberId, dataPlane = 'customer') => {
  const query = `?data_plane=${encodeURIComponent(dataPlane)}`;
  const config = {
    url: `${apiServerUrl}/api/datasets/default/check-member/${memberId}${query}`,
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get ALL datasets (admin-only, includes inactive)
 */
export const getAllDatasets = async (token, dataPlane = 'internal') => {
  const query = `?data_plane=${encodeURIComponent(dataPlane)}`;
  const config = {
    url: `${apiServerUrl}/api/datasets/all${query}`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get available models
 */
export const getAvailableModels = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/onyxai/models/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Get user's preferred model
 */
export const getPreferredModel = async (token) => {
  const config = {
    url: `${apiServerUrl}/api/me/preferences/model/`,
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

/**
 * Save user's preferred model
 */
export const setPreferredModel = async (token, modelId) => {
  const config = {
    url: `${apiServerUrl}/api/me/preferences/model/`,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { model_id: modelId },
  };

  const { data, error } = await callExternalApi({ config });
  return { data: data || null, error };
};

